import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { calculateChange, formatNumber, formatPercentage, formatDuration } from '../../utils/analytics-helpers';
import './MetricCard.css';

/**
 * MetricCard - Display a metric with optional comparison
 * @param {string} title - Metric title
 * @param {number} value - Current value
 * @param {number} previousValue - Previous period value (optional)
 * @param {string} format - Format type: 'number', 'percentage', 'duration', 'currency'
 * @param {React.Component} icon - Lucide icon component
 * @param {string} subtitle - Additional context text
 * @param {boolean} showTrend - Show trend indicator
 */
const MetricCard = ({
  title,
  value,
  previousValue,
  format = 'number',
  icon: Icon,
  subtitle,
  showTrend = true
}) => {
  // Format the value based on type
  const formatValue = (val) => {
    if (val === null || val === undefined) return '—';

    switch (format) {
      case 'number':
        return formatNumber(val);
      case 'percentage':
        return formatPercentage(val, 1, false);
      case 'duration':
        return formatDuration(val);
      case 'currency':
        return `$${formatNumber(val)}`;
      default:
        return val.toLocaleString();
    }
  };

  // Calculate change if previous value provided
  const change = previousValue !== undefined && previousValue !== null
    ? calculateChange(value, previousValue)
    : null;

  // Get trend icon
  const TrendIcon = change?.type === 'increase' ? TrendingUp :
                    change?.type === 'decrease' ? TrendingDown :
                    Minus;

  return (
    <div className="metric-card">
      <div className="metric-card-header">
        {Icon && (
          <div className="metric-card-icon">
            <Icon size={24} />
          </div>
        )}
        <div className="metric-card-title-section">
          <h4 className="metric-card-title">{title}</h4>
          {subtitle && <p className="metric-card-subtitle">{subtitle}</p>}
        </div>
      </div>

      <div className="metric-card-body">
        <div className="metric-card-value">{formatValue(value)}</div>

        {change && showTrend && (
          <div className={`metric-card-trend ${change.type}`}>
            <TrendIcon size={16} />
            <span>{Math.abs(change.percentage).toFixed(1)}%</span>
          </div>
        )}
      </div>

      {change && showTrend && (
        <div className="metric-card-footer">
          <span className="metric-card-comparison">
            {change.type === 'increase' ? '+' : change.type === 'decrease' ? '' : ''}
            {formatValue(change.absolute)} vs previous period
          </span>
        </div>
      )}
    </div>
  );
};

export default MetricCard;
